import { useState } from 'react';

import { Switch } from 'cpool-components/input/Switch';
import { MAIL_FAILURE_REASONS, STATUS_MAIL_PREVIEW_KINDS } from 'cpool-constants';
import type { MAIL_FAILURE_REASON } from 'cpool-constants';
import type { StatusMailPreviewResponse } from 'cpool-schemas';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import { LuCircleAlert } from 'react-icons/lu';

import { cn } from '~/utils/cnTailwind';

const HINTS = {
    CONTACT_MISSING: {
        text: 'STATUS.SEND_MAIL.PREVIEW.HINTS.CONTACT_MISSING.TEXT',
        title: 'STATUS.SEND_MAIL.PREVIEW.HINTS.CONTACT_MISSING.TITLE',
    },
    DIVISION_ID_NOT_FOUND: {
        text: 'STATUS.SEND_MAIL.PREVIEW.HINTS.DIVISION_ID_NOT_FOUND.TEXT',
        title: 'STATUS.SEND_MAIL.PREVIEW.HINTS.DIVISION_ID_NOT_FOUND.TITLE',
    },
    FALLBACK: {
        text: 'STATUS.SEND_MAIL.PREVIEW.HINTS.FALLBACK.TEXT',
        title: 'STATUS.SEND_MAIL.PREVIEW.HINTS.FALLBACK.TITLE',
    },
    LOAN_REQUEST_NOT_ASSIGNED: {
        text: 'STATUS.SEND_MAIL.PREVIEW.HINTS.LOAN_REQUEST_NOT_ASSIGNED.TEXT',
        title: 'STATUS.SEND_MAIL.PREVIEW.HINTS.LOAN_REQUEST_NOT_ASSIGNED.TITLE',
    },
    MISSING_CANCEL_TEMPLATE: {
        text: 'STATUS.SEND_MAIL.PREVIEW.HINTS.MISSING_CANCEL_TEMPLATE.TEXT',
        title: 'STATUS.SEND_MAIL.PREVIEW.HINTS.MISSING_CANCEL_TEMPLATE.TITLE',
    },
    MISSING_DIVISION_TEMPLATE: {
        text: 'STATUS.SEND_MAIL.PREVIEW.HINTS.MISSING_DIVISION_TEMPLATE.TEXT',
        title: 'STATUS.SEND_MAIL.PREVIEW.HINTS.MISSING_DIVISION_TEMPLATE.TITLE',
    },
    NO_MAIL: {
        text: 'STATUS.SEND_MAIL.PREVIEW.HINTS.NO_MAIL.TEXT',
        title: 'STATUS.SEND_MAIL.PREVIEW.HINTS.NO_MAIL.TITLE',
    },
} as const;

const HINTS_BY_REASON: Partial<Record<MAIL_FAILURE_REASON, (typeof HINTS)[keyof typeof HINTS]>> = {
    [MAIL_FAILURE_REASONS.DIVISION_ID_NOT_FOUND]: HINTS.DIVISION_ID_NOT_FOUND,
    [MAIL_FAILURE_REASONS.LOAN_REQUEST_CONTACT_NOT_FOUND]: HINTS.CONTACT_MISSING,
    [MAIL_FAILURE_REASONS.LOAN_REQUEST_NOT_ASSIGNED]: HINTS.LOAN_REQUEST_NOT_ASSIGNED,
    [MAIL_FAILURE_REASONS.MISSING_APPLICANT_CONTACT_DATA]: HINTS.CONTACT_MISSING,
    [MAIL_FAILURE_REASONS.MISSING_DIVISION_TEMPLATE]: HINTS.MISSING_DIVISION_TEMPLATE,
    [MAIL_FAILURE_REASONS.MISSING_MAIL_TEMPLATE]: HINTS.MISSING_CANCEL_TEMPLATE,
    [MAIL_FAILURE_REASONS.MISSING_TEMPLATE_META_DATA]: HINTS.MISSING_CANCEL_TEMPLATE,
    [MAIL_FAILURE_REASONS.USER_NOT_FOUND]: HINTS.DIVISION_ID_NOT_FOUND,
};

const getHint = (preview: Exclude<StatusMailPreviewResponse, { kind: typeof STATUS_MAIL_PREVIEW_KINDS.PREVIEW }>) =>
    preview.kind === STATUS_MAIL_PREVIEW_KINDS.NO_MAIL
        ? HINTS.NO_MAIL
        : HINTS_BY_REASON[preview.reason] ?? HINTS.FALLBACK;

type Props = {
    emptyText: string;
    isDimmed?: boolean;
    isError: boolean;
    // first load only: no previous preview to keep showing
    isLoading: boolean;
    // reload for a changed selection: previous preview stays visible to avoid layout jumps
    isRefreshing?: boolean;
    preview: StatusMailPreviewResponse | undefined;
};

export const StatusMailPreview: React.FC<Props> = ({
    emptyText,
    isDimmed = false,
    isError,
    isLoading,
    isRefreshing = false,
    preview,
}) => {
    const { t } = useTranslation();
    const [isHtml, setIsHtml] = useState(true);

    const renderEmptyState = (title: string, text?: string) => (
        <div className="flex flex-col items-center justify-center flex-1 gap-3 p-6 text-center bg-white border border-dashed rounded-md border-grey-outline-light">
            <span className="flex items-center justify-center w-12 h-12 rounded-full bg-surface-light">
                <LuCircleAlert className="w-5 h-5 text-midnight" />
            </span>
            <p className="text-sm font-bold text-midnight">{title}</p>
            {text && <p className="max-w-sm text-sm text-midnight">{text}</p>}
        </div>
    );

    const renderContent = () => {
        if (isError) return renderEmptyState(t('STATUS.SEND_MAIL.PREVIEW.LOADING_FAILED'));
        if (isLoading)
            return (
                <div className="flex flex-col flex-1 gap-3 animate-pulse" data-testid="status-mail-preview-loading">
                    <div className="h-8 bg-white rounded" />
                    <div className="flex-1 bg-white rounded-md" />
                </div>
            );
        if (!preview) return renderEmptyState(emptyText);
        if (preview.kind !== STATUS_MAIL_PREVIEW_KINDS.PREVIEW) {
            const hint = getHint(preview);
            return renderEmptyState(t(hint.title), t(hint.text));
        }

        return (
            <div className="flex flex-col flex-1 min-h-0 gap-3" data-testid="status-mail-preview-content">
                <div
                    aria-label={t('STATUS.SEND_MAIL.PREVIEW.SUBJECT')}
                    className="flex items-center h-8 px-3 text-sm bg-white border rounded border-grey-stroke text-midnight"
                >
                    <span className="truncate">{preview.subject}</span>
                </div>
                <div className="flex-1 min-h-0 py-[18px] pl-3 pr-[18px] overflow-auto text-sm bg-white border rounded-md border-grey-stroke text-midnight">
                    {isHtml ? (
                        <div
                            // mail clients keep default paragraph spacing, tailwind preflight resets it
                            className="[&_img]:max-w-full [&_p]:my-[1em] [&_table]:max-w-full"
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(preview.html, { ADD_TAGS: ['style'], FORCE_BODY: true }),
                            }}
                        />
                    ) : (
                        <p className="break-words whitespace-pre-wrap">{preview.text}</p>
                    )}
                </div>
            </div>
        );
    };

    return (
        <section
            aria-label={t('STATUS.SEND_MAIL.PREVIEW.TITLE')}
            // fills the dialog column: preview, hint and loading state must not resize the dialog
            className={cn(
                'flex flex-col h-full min-h-0 gap-[18px] px-[18px] pt-3 pb-[18px] rounded-md bg-surface-light transition-opacity',
                // "Nein" dims the whole panel, header included
                isDimmed && 'opacity-40',
            )}
            data-testid="status-mail-preview"
        >
            <div className="flex items-center justify-between gap-3">
                <h4 className="text-base font-bold text-midnight">{t('STATUS.SEND_MAIL.PREVIEW.TITLE')}</h4>
                <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-midnight">{t('STATUS.SEND_MAIL.PREVIEW.HTML_VIEW')}</span>
                    <Switch
                        id="status-mail-preview-html"
                        aria-label={t('STATUS.SEND_MAIL.PREVIEW.HTML_VIEW')}
                        checked={isHtml}
                        onChange={(event) => setIsHtml(event.target.checked)}
                    />
                </div>
            </div>
            <div
                aria-busy={isRefreshing}
                className={cn('flex flex-col flex-1 min-h-0 transition-opacity', isRefreshing && 'opacity-60')}
                data-testid="status-mail-preview-body"
            >
                {renderContent()}
            </div>
        </section>
    );
};
